import React, {
  useState, useEffect, useContext,
} from 'react';
import axios from 'axios';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { baseUrl } from 'Api';
import { AppContext, AuthContext } from 'Context';
import { ReactComponent as VisibleIcon } from 'Assets/eye.svg';
import { ReactComponent as HiddenIcon } from 'Assets/eye.slash.svg';
import PaymentModalV2 from './PaymentModalV2';
import styles from './createUserV2.module.css';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isDeliverStreamPlan = (plan) => {
  const name = (plan?.product_name || '').trim().toLowerCase();
  return name === 'deliver & stream' || name === 'deliver and stream' || Number(plan?.amount) === 0;
};

// New, self-contained "create a user" page (quick + withPlan). The payment step
// opens as a modal (PaymentModalV2). "Enter to account" reuses the same
// manager-switch logic as the legacy page (kept behaviourally identical).
const CreateUserV2 = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const userType = params.get('type') === 'withPlan' ? 'withPlan' : 'quick';

  const { getToken } = useContext(AuthContext);
  const {
    setapiCALL, handleClick, getSubscription, autoSignIn,
    setAffliatePlanId, setCurrentSelectedPlan,
  } = useContext(AppContext);
  const myToken = getToken();

  const [email, setEmail] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [numberErr, setNumberErr] = useState('');

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const [loader, setLoader] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  const hasUpper = /[A-Z]/.test(password);
  const pwStrong = password.length >= 12 && hasUpper && /\d/.test(password) && /[^A-Za-z0-9]/.test(password);

  useEffect(() => {
    setPlansLoading(true);
    axios
      .post(`${baseUrl}/getAllActivePlans`, {
        for_affiliate: true,
        token: myToken,
        // Quick-user flow lists a different (quick) plan set.
        ...(userType === 'quick' && { quick_plans: true }),
      })
      .then((res) => setPlans(res.data?.success && Array.isArray(res.data.data) ? res.data.data : []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const onEmail = (v) => {
    setEmail(v);
    setEmailExists(false);
    if (v.includes('@') && v.includes('.')) {
      setEmailChecking(true);
      axios.post(`${baseUrl}/signupEmail`, { email: v })
        .then((r) => setEmailExists(r.data))
        .catch(() => {})
        .finally(() => setEmailChecking(false));
    }
  };

  const validPhone = (p) => {
    if (!p) return false;
    // Proper per-country length check (handles international numbers, e.g. FI).
    try {
      if (isPossiblePhoneNumber(p)) return true;
    } catch (e) { /* fall through to the length fallback */ }
    // Fallback: E.164 allows up to 15 digits (min ~8 incl. country code).
    const d = p.replace(/\D/g, '');
    return d.length >= 8 && d.length <= 15;
  };
  const textOnly = (v) => /^[a-zA-Z\s]*$/.test(v);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  const canContinue = emailRegex.test(email)
    && emailExists?.message !== 'Email already exists'
    && !!selectedPlanId
    && (userType === 'quick'
      || (firstName && lastName && validPhone(phone) && pwStrong))
    && !loader;

  // "Enter to account" — reused from the legacy flow, unchanged in behaviour.
  const switchToUserAccount = async (u) => {
    try {
      const fd = new FormData();
      fd.append('token', myToken);
      fd.append('user_id', u.userId || u.id);
      fd.append('user_token', u.token);
      const res = await axios.post(`${baseUrl}/managerUserLogs`, fd);
      if (res?.data?.success === true) {
        handleClick(u);
        localStorage.setItem('firstname', u?.firstname || '');
        localStorage.setItem('lastname', u?.lastname || '');
        localStorage.setItem('isAccessingUser', 'true');
        localStorage.setItem('accessedUserId', String(u?.userId || u?.id || ''));
        sessionStorage.setItem('quickUserEmail', email);
        navigate('/quick-user-upload', { replace: true });
        getSubscription();
        autoSignIn();
      } else {
        navigate('/plans');
      }
    } catch (e) {
      navigate('/plans');
    }
  };

  const handleContinue = async () => {
    setErr('');
    if (!emailRegex.test(email)) { setErr('Please enter a valid email address.'); return; }
    if (userType === 'withPlan') {
      if (!firstName || !lastName) { setErr('First and last name are required.'); return; }
      if (!validPhone(phone)) { setNumberErr('Please enter a valid phone number.'); return; }
      if (!pwStrong) { setErr('Please enter a strong password (12+ chars, upper, number, symbol).'); return; }
    }
    if (!selectedPlan) { setErr('Please select a plan.'); return; }

    const deliverStream = isDeliverStreamPlan(selectedPlan);
    setLoader(true);
    try {
      const payload = {
        token: myToken,
        email,
        no_plan: !deliverStream,
        ...(deliverStream && { plan_id: selectedPlanId }),
      };
      if (userType === 'withPlan') {
        payload.password = password;
        payload.firstname = firstName;
        payload.lastname = lastName;
        payload.mobile = phone;
      }
      const response = await axios.post(`${baseUrl}/userRegistration`, payload);
      setapiCALL(response.data);
      const created = response.data?.data?.userDetails?.[0];

      if (deliverStream) {
        if (response.data?.subscription === true && created) await switchToUserAccount(created);
        else navigate('/affiliate');
        return;
      }

      // Any other plan → payment modal. Prime the pieces the payment payload needs.
      const createdUserId = created?.userId || created?.id;
      if (createdUserId) localStorage.setItem('affliate_userId', String(createdUserId));
      setAffliatePlanId?.(selectedPlanId);
      setCurrentSelectedPlan?.(selectedPlan);
      localStorage.setItem('planId', String(selectedPlanId));
      setCreatedUser(created);
      setPayOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoader(false);
    }
  };

  const renderPlan = (plan) => {
    const aff = plan.affiliate;
    const full = Number(plan.amount);
    const eff = aff ? Number(aff.effective_amount) : full;
    const isFree = eff === 0;
    const discounted = eff < full;
    const selected = selectedPlanId === plan.id;
    let off = '';
    if (aff?.discount && !isFree) {
      const d = aff.discount;
      if (d.type === 'percentage' && d.value) off = `${d.value}% off`;
      else if (d.value) off = `$${(Number(d.value) / 100).toFixed(2)} off`;
    }
    return (
      <div
        key={plan.id}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
        onClick={() => setSelectedPlanId(plan.id)}
        className={`${styles.planOption} ${selected ? styles.planSelected : ''}`}
      >
        <span className={styles.radio} />
        <div className={styles.planInfo}>
          <span className={styles.planName}>{plan.product_name}</span>
          {plan.price_description && <span className={styles.planSub}>{plan.price_description}</span>}
        </div>
        <div className={styles.planPrice}>
          {isFree ? (
            <span className={styles.freeBadge}>FREE</span>
          ) : (
            <div className={styles.priceRow}>
              <span className={styles.eff}>{`$${eff.toFixed(2)}`}</span>
              {discounted && <span className={styles.full}>{`$${full.toFixed(2)}`}</span>}
            </div>
          )}
          {off && <span className={styles.offBadge}>{off}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate('/settings/manage-users')}>
        ← BACK
      </button>
      <div className={styles.card}>
        <h1 className={styles.title}>Create a new user</h1>
        <p className={styles.sub}>
          {userType === 'withPlan'
            ? 'Enter the user’s details and choose a plan.'
            : 'Enter the user’s email and choose a plan.'}
        </p>

        <div className={styles.field}>
          <span className={styles.label}>Email</span>
          <div className={styles.spinField}>
            <input
              className={styles.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              autoFocus
            />
            {emailChecking && <span className={styles.spinIcon} />}
          </div>
          {emailExists?.message === 'Email already exists' && (
            <p className={styles.err}>Email already exists</p>
          )}
        </div>

        {userType === 'withPlan' && (
          <>
            <div className={styles.field}>
              <div className={styles.row2}>
                <input
                  className={styles.input}
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => textOnly(e.target.value) && setFirstName(e.target.value)}
                />
                <input
                  className={styles.input}
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => textOnly(e.target.value) && setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.field}>
              <PhoneInput
                placeholder="Phone number"
                value={phone}
                onChange={(v) => { setPhone(v || ''); setNumberErr(''); }}
                countryCallingCodeEditable={false}
                defaultCountry="US"
                className={styles.input}
              />
              {numberErr && <p className={styles.err}>{numberErr}</p>}
            </div>
            <div className={styles.field}>
              <div className={styles.pwWrap}>
                <input
                  className={styles.input}
                  type={showPw ? 'text' : 'password'}
                  placeholder="Password"
                  maxLength={30}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="button" className={styles.peek} onClick={() => setShowPw((v) => !v)}>
                  {showPw ? <HiddenIcon width={20} /> : <VisibleIcon width={20} />}
                </button>
              </div>
              {password && !pwStrong && (
                <p className={styles.err}>Use 12+ characters with an uppercase letter, a number, and a symbol.</p>
              )}
              {password && pwStrong && <p className={`${styles.err} ${styles.hintOk}`}>Strong password ✓</p>}
            </div>
          </>
        )}

        <p className={styles.planLabel}>Select a plan</p>
        {plansLoading ? (
          <p className={styles.planState}>Loading plans…</p>
        ) : !plans.length ? (
          <p className={styles.planState}>No plans available.</p>
        ) : (
          <div className={styles.planList}>{plans.map(renderPlan)}</div>
        )}

        {err && <p className={styles.err} style={{ marginTop: 12 }}>{err}</p>}

        <button type="button" className={styles.primary} disabled={!canContinue} onClick={handleContinue}>
          {loader ? <span className={styles.spinCircle} /> : 'Continue'}
        </button>
      </div>

      <PaymentModalV2
        open={payOpen}
        onClose={() => setPayOpen(false)}
        plan={selectedPlan}
        user={createdUser}
        onPaid={() => switchToUserAccount(createdUser)}
      />
    </div>
  );
};

export default CreateUserV2;
